import {
  BILINGUAL_LIMITS,
  isTraditionalLocale,
  localeToContentLang,
  otherLang,
  type BilingualFieldType,
  type ContentLang,
} from '@yoyoclub/shared';
import { useCallback, useEffect, useId, useRef, useState, type RefObject } from 'react';

import { useAuth } from '../hooks/useAuth';
import { useSiteLanguage } from '../i18n/useSiteLanguage';
import { isTranslationEnabled, translateText } from '../services/translationApi';

import { applySideEdit, type BilingualValue } from './bilingualValue';
import { useTranslateAllFiller, type FillOutcome } from './translateAllContext';

const SIDE_LABEL: Record<ContentLang, string> = { ZH: 'Chinese', EN: 'English' };
const SIDE_KEY = { ZH: 'zh', EN: 'en' } as const;

interface Props {
  fieldType: BilingualFieldType;
  value: BilingualValue;
  onChange: (next: BilingualValue) => void;
  /** Base label, e.g. "Title"; each side shows "Title (Chinese)" / "Title (English)". */
  label: string;
  /** At least one language is required. */
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  placeholder?: Partial<Record<ContentLang, string>>;
  disabled?: boolean;
  /** Fill an empty side on mount when the other side has text (edit forms). */
  translateOnMount?: boolean;
  /** Refs to the textareas, for cursor-position inserts. */
  inputRefs?: Partial<Record<ContentLang, RefObject<HTMLTextAreaElement>>>;
  onFocusSide?: (side: ContentLang) => void;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  /** Tighter spacing for list rows (session rows, popovers). */
  compact?: boolean;
  /** One column instead of two (narrow containers). */
  stacked?: boolean;
  /** Full per-side labels, replacing "<label> (Chinese)" / "<label> (English)". */
  sideLabels?: Partial<Record<ContentLang, string>>;
  [dataAttr: `data-${string}`]: string | undefined;
}

const DEFAULT_INPUT =
  'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400';

export function BilingualField({
  fieldType,
  value,
  onChange,
  label,
  required,
  multiline,
  rows = 6,
  placeholder,
  disabled,
  translateOnMount = true,
  inputRefs,
  onFocusSide,
  className = '',
  inputClassName = DEFAULT_INPUT,
  labelClassName = 'block text-sm font-medium text-gray-700 mb-1',
  compact,
  stacked,
  sideLabels,
  ...rest
}: Props) {
  const { getToken } = useAuth();
  const idBase = useId();
  // The user's language comes first and sets the script of machine Chinese (PRD §4.1, §6.2).
  const siteLanguage = useSiteLanguage();
  const languageRef = useRef(siteLanguage);
  languageRef.current = siteLanguage;
  const firstSide = localeToContentLang(siteLanguage);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState<Partial<Record<ContentLang, boolean>>>({});
  const [failed, setFailed] = useState<Partial<Record<ContentLang, boolean>>>({});
  // Latest value, for stale-result checks after an await.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Source text each side was last translated from, so blur only retranslates on change.
  const lastSource = useRef<Partial<Record<ContentLang, string>>>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void isTranslationEnabled().then((on) => mounted.current && setEnabled(on));
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Translate `from` into the other side. The result is applied only if the
   * source is unchanged and the target was not edited meanwhile (PRD §4.2).
   */
  const translate = useCallback(
    async (from: ContentLang): Promise<FillOutcome> => {
      const to = otherLang(from);
      const source = valueRef.current[SIDE_KEY[from]].trim();
      if (!source) return 'skipped';
      const targetAtRequest = valueRef.current[SIDE_KEY[to]];
      const languageAtRequest = languageRef.current;
      lastSource.current[from] = source;
      setBusy((b) => ({ ...b, [to]: true }));
      setFailed((f) => ({ ...f, [to]: false }));
      let retryInNewScript = false;
      try {
        const token = await getToken();
        if (!token) throw new Error('not signed in');
        const zhScript = isTraditionalLocale(languageAtRequest) ? 'traditional' : 'simplified';
        const text = await translateText(fieldType, from, source, token, zhScript);
        if (!mounted.current) return 'skipped';
        const now = valueRef.current;
        if (now[SIDE_KEY[from]].trim() !== source || now[SIDE_KEY[to]] !== targetAtRequest) {
          return 'skipped'; // the host kept typing; never overwrite newer input
        }
        if (
          to === 'ZH' &&
          isTraditionalLocale(languageRef.current) !== isTraditionalLocale(languageAtRequest)
        ) {
          // Chinese in the previous script: drop it and ask again in the new one,
          // since source and target are unchanged and still eligible (PRD §6.2).
          // English and Simplified both mean Simplified, so only a script change counts.
          retryInNewScript = true;
          return 'skipped';
        }
        onChangeRef.current({ ...now, [SIDE_KEY[to]]: text, autoLang: to });
        return 'filled';
      } catch {
        if (mounted.current) setFailed((f) => ({ ...f, [to]: true }));
        return 'failed';
      } finally {
        if (mounted.current) setBusy((b) => ({ ...b, [to]: false }));
        if (retryInNewScript && mounted.current) void translateRef.current(from);
      }
    },
    [fieldType, getToken]
  );
  const translateRef = useRef(translate);
  translateRef.current = translate;

  // "Translate all" (TranslateAll.tsx): fill the other side when it is empty or was
  // machine-translated; a side someone wrote is never replaced.
  useTranslateAllFiller(async () => {
    if (!enabled || disabled) return 'skipped';
    const v = valueRef.current;
    const zh = v.zh.trim();
    const en = v.en.trim();
    if (zh && (!en || v.autoLang === 'EN')) return translate('ZH');
    if (en && (!zh || v.autoLang === 'ZH')) return translate('EN');
    return 'skipped';
  });

  // Edit forms: an empty side next to a filled one gets translated once on open.
  const mountTranslated = useRef(false);
  useEffect(() => {
    if (!enabled || !translateOnMount || disabled || mountTranslated.current) return;
    mountTranslated.current = true;
    const zh = value.zh.trim();
    const en = value.en.trim();
    if (zh && !en) void translate('ZH');
    else if (en && !zh) void translate('EN');
    // Only on the first enabled render by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const setSide = (side: ContentLang, text: string) => onChange(applySideEdit(value, side, text));

  const onBlur = (side: ContentLang) => {
    if (!enabled || disabled) return;
    const other = otherLang(side);
    const source = value[SIDE_KEY[side]].trim();
    if (!source || source === lastSource.current[side]) return;
    const otherText = value[SIDE_KEY[other]].trim();
    if (otherText === '' || value.autoLang === other) void translate(side);
  };

  const manual = (to: ContentLang) => {
    const from = otherLang(to);
    const target = value[SIDE_KEY[to]].trim();
    if (target && value.autoLang !== to) {
      const ok = window.confirm(
        `Replace your ${SIDE_LABEL[to]} text with a translation from ${SIDE_LABEL[from]}?`
      );
      if (!ok) return;
    }
    void translate(from);
  };

  const renderSide = (side: ContentLang) => {
    const other = otherLang(side);
    const key = SIDE_KEY[side];
    const text = value[key];
    const isMachine = value.autoLang === side && text.trim() !== '';
    const sourceIsMachine = value.autoLang === other;
    const canManual =
      enabled && !disabled && value[SIDE_KEY[other]].trim() !== '' && !sourceIsMachine;
    const inputId = `${idBase}-${side}`;
    const common = {
      id: inputId,
      value: text,
      disabled,
      maxLength: BILINGUAL_LIMITS[fieldType][side],
      placeholder: placeholder?.[side],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setSide(side, e.target.value),
      onBlur: () => onBlur(side),
      onFocus: () => onFocusSide?.(side),
      'aria-busy': busy[side] || undefined,
      className: `${inputClassName}${multiline ? ' font-mono text-sm' : ''}`,
    };
    return (
      <div key={side} className="min-w-0">
        <div className="flex items-end justify-between gap-2">
          <label htmlFor={inputId} className={labelClassName}>
            {sideLabels?.[side] ?? `${label} (${SIDE_LABEL[side]})`}
            {required && <span className="text-red-500"> *</span>}
          </label>
          {canManual && (
            <button
              type="button"
              onClick={() => manual(side)}
              disabled={busy[side]}
              className="mb-1 text-xs text-primary hover:underline disabled:opacity-50 whitespace-nowrap"
            >
              Translate from {SIDE_LABEL[other]}
            </button>
          )}
        </div>
        {multiline ? (
          <textarea ref={inputRefs?.[side]} rows={rows} {...common} />
        ) : (
          <input type="text" {...common} />
        )}
        {busy[side] && <p className="mt-1 text-xs text-gray-500">Translating...</p>}
        {!busy[side] && failed[side] && !isMachine && (
          <p className="mt-1 text-xs text-gray-500">
            Translation unavailable. You can fill this in yourself or save as is.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className={className} {...rest}>
      <div
        className={`grid grid-cols-1 ${stacked ? 'gap-3' : compact ? 'gap-3 sm:grid-cols-2' : 'gap-6 md:grid-cols-2'}`}
      >
        {renderSide(firstSide)}
        {renderSide(otherLang(firstSide))}
      </div>
      {required && !compact && (
        <p className="mt-1 text-xs text-gray-500">
          {enabled
            ? 'Fill in at least one language. The other is translated for you to review.'
            : 'Fill in at least one language.'}
        </p>
      )}
    </div>
  );
}
